import { useState, type FormEvent } from 'react';
import {
  apiErrorText,
  type CeoBankConnectResult,
  useFinishCeoBankOauthMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { WrapButton } from '../ui';
import { ErrorNotice, FormActions, FormBox, FormHint, FormTitle, Input } from './styles';

/** Ответ банка из адреса, на который он вернул браузер. */
function readReturnAddress(address: string) {
  try {
    const { searchParams } = new URL(address.trim());
    return {
      code: searchParams.get('code'),
      state: searchParams.get('state'),
      error: searchParams.get('error'),
    };
  } catch {
    return null;
  }
}

interface SandboxLoginFormProps {
  authorizeUrl: string;
  onDone: (result: CeoBankConnectResult) => void;
  onClose: () => void;
}

/**
 * Вход в песочницу банка. Тестовое приложение возвращает браузер на `localhost`, где сайта нет,
 * поэтому код входа человек переносит сюда сам, из адресной строки браузера.
 */
export function SandboxLoginForm({ authorizeUrl, onDone, onClose }: SandboxLoginFormProps) {
  const [finishOauth, { isLoading }] = useFinishCeoBankOauthMutation();
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const answer = readReturnAddress(address);
    if (answer?.error) {
      setError(`Банк не выдал доступ (${answer.error}). Откройте вход и повторите.`);
      return;
    }
    if (!answer?.code || !answer.state) {
      setError('В этом адресе нет кода банка. Скопируйте адрес из строки браузера целиком.');
      return;
    }
    try {
      setError(null);
      onDone(await finishOauth({ code: answer.code, state: answer.state }).unwrap());
    } catch (err: unknown) {
      // Код и ссылка одноразовые: повторить можно только новым входом.
      const reason = apiErrorText(err, 'Не удалось завершить вход в песочницу');
      setError(`${reason} Чтобы повторить, нажмите «Отмена», затем «Войти в банк».`);
    }
  }

  return (
    <FormBox as="form" onSubmit={handleSubmit}>
      <FormTitle>Вход в песочницу банка</FormTitle>
      <FormHint>
        1. Откройте вход и войдите тестовым пользователем банка: логин есть на портале Alfa API, на
        странице о тестировании.
      </FormHint>
      <FormActions>
        <WrapButton
          $size="sm"
          $variant="secondary"
          type="button"
          onClick={() => window.open(authorizeUrl, '_blank', 'noopener')}
        >
          Открыть вход в песочницу
        </WrapButton>
      </FormActions>
      <FormHint>
        2. После входа браузер покажет ошибку страницы localhost: так и должно быть. Скопируйте
        адрес из строки браузера и вставьте сюда. Код банка живёт две минуты.
      </FormHint>
      <Input
        placeholder="http://localhost/?code=…"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        required
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      <FormActions>
        <WrapButton $size="sm" type="submit" disabled={isLoading}>
          {isLoading ? 'Проверяем у банка…' : 'Завершить проверку'}
        </WrapButton>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose}>
          Отмена
        </Button>
      </FormActions>
    </FormBox>
  );
}
